import { forwardRef } from 'react';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react';

// DESIGN-SYSTEM.md §5.8 "Toasts" / design-system.html `.toast`:
// bottom-left, dark pill (bg = text color / text = bg color in light mode,
// flips to surface+border in dark mode), radius 12px, 13px/700 text, single
// leading icon colored by severity, no close button — the timer/clickaway on
// the parent `Snackbar` is the only way it goes away. Success is the app's
// only "success" feedback mechanism per the spec; error/warning/info reuse
// the same pill, only the icon (and its color) changes so failures stay
// visually distinguishable.
const SEVERITY_ICON = {
    success: CheckCircle2,
    error: XCircle,
    warning: AlertTriangle,
    info: Info,
};

// `Snackbar`'s default `Grow` transition (react-transition-group) needs a
// ref to the underlying DOM node (it calls `node.scrollTop` to force a
// reflow before animating in). A plain function component silently drops
// that ref, leaving `node` null and crashing the transition the moment the
// toast tries to open — must forward the ref down to `Alert` (which in turn
// forwards it to its root element).
const ToastAlert = forwardRef(function ToastAlert({ severity = 'success', children, sx, ...rest }, ref) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const Icon = SEVERITY_ICON[severity] || CheckCircle2;
    const iconColor = theme.palette[severity]?.main || theme.palette.success.main;

    return (
        <Alert
            ref={ref}
            severity={severity}
            variant="standard"
            icon={<Icon size={18} strokeWidth={2} color={iconColor} />}
            sx={{
                alignItems: 'center',
                gap: '10px',
                borderRadius: '12px',
                padding: '13px 18px',
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: 1.3,
                backgroundColor: isDark ? theme.palette.background.paper : theme.palette.text.primary,
                color: isDark ? theme.palette.text.primary : theme.palette.background.default,
                border: isDark ? `1px solid ${theme.palette.divider}` : 'none',
                boxShadow: theme.customShadows?.lg,
                '& .MuiAlert-icon': {
                    padding: 0,
                    marginRight: 0,
                    opacity: 1,
                },
                '& .MuiAlert-message': {
                    padding: 0,
                },
                // The design system toast has no dismiss button — it only
                // goes away via the auto-hide timer/clickaway on the parent
                // Snackbar. Guard against a stray `onClose` still rendering
                // MUI's built-in close action.
                '& .MuiAlert-action': {
                    display: 'none',
                },
                ...sx,
            }}
            {...rest}
        >
            {children}
        </Alert>
    );
});

export default ToastAlert;
